import { useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { MdOpenInNew } from 'react-icons/md';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useSetError } from '@src/redux/modals';
import { Brand60, Danger500, Neutral300, Neutral600 } from '@src/utils/colors';
import { Button } from '@Components/Button';
import { Appearance } from '@Components/designConstants';
import { ChartContainer } from '@Components/ChartContainer';
import { DataCard } from '@Components/DataCard';
import { EmptyState } from '@Components/EmptyState';
import { Spinner } from '@Components/Spinner';
import { AdRawEffectStatistics, StageOnePoint } from './linear-ml-diagnostics-constants';
import { computeTicks } from './linear-ml-diagnostics-utils';
import { useStageOneDiagnostics } from './use-linear-ml-diagnostics';
import { PdfPreview } from './PdfPreview';
import './StageOneDiagnostics.scss';

const TICK_DATE_FORMAT = 'M/yyyy';
const LONG_DATE_FORMAT = 'M/dd/yyyy h:mm:ss a';
const AXIS_TICK = { fill: Neutral600, fontSize: 12 };
const OBJECT_URL_LIFETIME_MS = 60_000;

// parseFloat, not Number, so a missing stat reads NaN instead of a misleading 0.00.
const formatStat = (value: string | number | null): string => parseFloat(String(value)).toFixed(2);

const STATISTICS: { label: string; getValue: (stats: AdRawEffectStatistics) => string }[] = [
  { label: 'Min', getValue: stats => formatStat(stats.min) },
  { label: 'Max', getValue: stats => formatStat(stats.max) },
  { label: 'Mean', getValue: stats => formatStat(stats.mean) },
  { label: 'Median', getValue: stats => formatStat(stats.median) },
  { label: 'Std. deviation', getValue: stats => formatStat(stats.stdev) },
  { label: 'Kurtosis', getValue: stats => formatStat(stats.kurtosis) },
  { label: 'Std. devs to min', getValue: stats => formatStat(stats.minMedianStDev) },
  { label: 'Std. devs to max', getValue: stats => formatStat(stats.maxMedianStDev) }
];

interface StageOneDiagnosticsProps {
  kpi: string | undefined;
  branch: string;
}

export const StageOneDiagnostics = ({ kpi, branch }: StageOneDiagnosticsProps) => {
  const setError = useSetError();
  const { data, error, isError } = useStageOneDiagnostics(kpi, branch);

  useEffect(() => {
    if (error) {
      setError({ message: error.message, reportError: error });
    }
  }, [error, setError]);

  if (isError) {
    return (
      <div className='stage-one-diagnostics'>
        <EmptyState title="Couldn't load Stage One data" description={error.message} />
      </div>
    );
  }
  if (!data) {
    return (
      <div className='stage-one-diagnostics loading'>
        <Spinner />
      </div>
    );
  }
  if (!data.allValues) {
    return (
      <div className='stage-one-diagnostics'>
        <EmptyState
          title='No Stage One data'
          description={`There is no stage one output for ${kpi} on branch ${branch}.`}
        />
      </div>
    );
  }

  const { adRawEffectStatistics: stats } = data;
  return (
    <div className='stage-one-diagnostics'>
      <section
        className='summary-statistics'
        aria-label='AdRawEffect summary'
        data-testid='summary-statistics'
      >
        {STATISTICS.map(({ label, getValue }) => (
          <DataCard key={label} label={label} value={getValue(stats)} />
        ))}
      </section>
      <div className='stage-one-charts'>
        <ChartContainer
          title='AdRawEffect Moving Average'
          titleAfterDashText={`${stats.startDate} – ${stats.endDate}`}
        >
          <MovingAverageChart
            values={data.allValues}
            percentile1={data.percentile1}
            percentile99={data.percentile99}
          />
        </ChartContainer>
        <ChartContainer
          title='Smoothed Impulse Response Curve'
          viewRightActions={Boolean(data.pdf)}
          rightActions={data.pdf && <OpenPdfButton pdf={data.pdf} />}
        >
          {data.pdf ? (
            <PdfPreview pdf={data.pdf} title='Smoothed impulse response curve' />
          ) : (
            <EmptyState
              title='No curve available'
              description="The impulse response PDF couldn't be loaded for this KPI and branch."
            />
          )}
        </ChartContainer>
      </div>
    </div>
  );
};

interface MovingAverageChartProps {
  values: StageOnePoint[];
  percentile1: number;
  percentile99: number;
}

const MovingAverageChart = ({ values, percentile1, percentile99 }: MovingAverageChartProps) => {
  const ticks = useMemo(() => computeTicks(values), [values]);
  return (
    <div className='moving-average-chart' data-testid='moving-average-chart'>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart data={values} margin={{ top: 16, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid vertical={false} stroke={Neutral300} />
          <XAxis
            dataKey='time'
            type='number'
            allowDecimals={false}
            domain={['dataMin', 'dataMax']}
            ticks={ticks}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: Neutral300 }}
            tickFormatter={time => format(new Date(time), TICK_DATE_FORMAT)}
          />
          <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={56} />
          <Tooltip
            isAnimationActive={false}
            labelFormatter={time => format(new Date(Number(time)), LONG_DATE_FORMAT)}
            formatter={value => [Number(value).toLocaleString(), 'Moving average']}
          />
          <ReferenceLine
            y={percentile99}
            stroke={Danger500}
            strokeDasharray='4 4'
            label={{
              position: 'insideTopRight',
              value: '99th percentile',
              fill: Danger500,
              fontSize: 12
            }}
          />
          <ReferenceLine
            y={percentile1}
            stroke={Danger500}
            strokeDasharray='4 4'
            label={{
              position: 'insideBottomRight',
              value: '1st percentile',
              fill: Danger500,
              fontSize: 12
            }}
          />
          <Line
            dataKey='moving_average'
            type='monotone'
            stroke={Brand60}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

interface OpenPdfButtonProps {
  pdf: Blob;
}

// Opens the PDF from the bytes already downloaded, so the link never depends on a signed URL that
// may have expired. The object URL is released once the new tab has had time to load it.
const OpenPdfButton = ({ pdf }: OpenPdfButtonProps) => (
  <Button
    appearance={Appearance.OUTLINED}
    trailingElement={<MdOpenInNew />}
    data-testid='open-pdf-button'
    onClick={() => {
      const url = URL.createObjectURL(pdf);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
    }}
  >
    Open PDF
  </Button>
);
